import { useState, useEffect } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  ReceiptEuro,
  Users,
  Tag,
  Calendar,
  Sliders,
  BookMarked,
  Sun,
  Moon,
  LogOut,
  Menu,
  Receipt,
  Building2,
  HelpCircle,
  Sparkles,
  Truck,
  Mail,
  HardHat,
  MessageCircle,
  Wallet,
  Home,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import InstallPrompt, { InstallAppButton } from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";
import PushToggle from "@/components/PushToggle";
import { useSettings } from "@/lib/useSettings";
import TrialBanner from "@/components/TrialBanner";
import TrialExpired from "@/components/TrialExpired";
import OnboardingTour from "@/components/OnboardingTour";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ServerWakingBanner, PreviewBanner } from "@/components/AppBanners";
import { LANGS, getLang } from "@/i18n";
import { isSimple, SIMPLE_PATHS } from "@/lib/edition";

const NAV = [
  { to: "/", label: "Panoramica", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
  { to: "/assistente", label: "Assistente IA", icon: Sparkles, testid: "sidebar-nav-agent", adminOnly: true },
  { to: "/attivita-oggi", label: "Attività del giorno", icon: Sun, testid: "sidebar-nav-today", adminOnly: true },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "sidebar-nav-quotes" },
  { to: "/fatture", label: "Fatture", icon: ReceiptEuro, testid: "sidebar-nav-invoices", adminOnly: true },
  { to: "/pagamenti-incassi", label: "Pagamenti e incassi", icon: Wallet, testid: "sidebar-nav-payments", adminOnly: true },
  { to: "/ddt", label: "DDT / Bolle", icon: Truck, testid: "sidebar-nav-ddt" },
  { to: "/spese", label: "Spese di Gestione", icon: Receipt, testid: "sidebar-nav-expenses", adminOnly: true },
  { to: "/contatti", label: "Contatti & Clienti", icon: Users, testid: "sidebar-nav-contacts" },
  { to: "/listino", label: "Listino Prezzi", icon: Tag, testid: "sidebar-nav-price-list" },
  { to: "/preziario", label: "Preziari", icon: BookMarked, testid: "sidebar-nav-preziario" },
  { to: "/agenda", label: "Agenda & Scadenze", icon: Calendar, testid: "sidebar-nav-agenda" },
  { to: "/cantieri", label: "Operai e Cantieri", icon: HardHat, testid: "sidebar-nav-sites", adminOnly: true },
  { to: "/posta", label: "Posta", icon: Mail, testid: "sidebar-nav-mail", adminOnly: true },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "sidebar-nav-whatsapp", adminOnly: true },
  { to: "/impostazioni", label: "Impostazioni Brand", icon: Sliders, testid: "sidebar-nav-settings", adminOnly: true },
  { to: "/aziende", label: "Aziende (Super Admin)", icon: Building2, testid: "sidebar-nav-companies", superOnly: true },
];

const TITLES = {
  "/": "Panoramica",
  "/attivita-oggi": "Attività del giorno",
  "/pagamenti-incassi": "Pagamenti e incassi",
  "/preventivi": "Preventivi",
  "/fatture": "Fatture",
  "/ddt": "DDT / Bolle",
  "/posta": "Posta",
  "/cantieri": "Operai e Cantieri",
  "/whatsapp": "WhatsApp",
  "/spese": "Spese di Gestione",
  "/contatti": "Contatti & Clienti",
  "/listino": "Listino Prezzi",
  "/preziario": "Preziari",
  "/agenda": "Agenda & Scadenze",
  "/assistente": "Assistente IA",
  "/impostazioni": "Impostazioni Brand",
  "/aziende": "Aziende registrate",
  "/abbonamento": "Continua con K Gestionale",
};

export default function Layout() {
  const { user, logout, setOnboarding } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const settings = useSettings();
  const companyName = settings?.ragione_sociale || "La mia azienda";
  const company = user?.company;
  const simple = isSimple(user);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (user?.role === "admin" && company && !company.is_default && !company.trial_expired && !user?.onboarding?.tour_done) {
      const t = setTimeout(() => setTourOpen(true), 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user?.role, user?.onboarding?.tour_done, company]);

  useEffect(() => {
    const start = () => setTourOpen(true);
    window.addEventListener("start-tour", start);
    return () => window.removeEventListener("start-tour", start);
  }, []);

  const closeTour = () => {
    setTourOpen(false);
    if (!user?.onboarding?.tour_done) setOnboarding({ tour_done: true });
  };

  useEffect(() => {
    const goOff = () => setOffline(true);
    const goOn = () => setOffline(false);
    window.addEventListener("offline", goOff);
    window.addEventListener("online", goOn);
    window.addEventListener("app-offline", goOff);
    return () => {
      window.removeEventListener("offline", goOff);
      window.removeEventListener("online", goOn);
      window.removeEventListener("app-offline", goOff);
    };
  }, []);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  if (company?.trial_expired && !user?.is_superadmin) return <TrialExpired company={company} onLogout={doLogout} isAdmin={user?.role === "admin"} />;

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
        {settings?.logo_url ? (
          <img src={settings.logo_url} alt={companyName} className="h-10 w-auto max-w-[72px] object-contain rounded-md" data-testid="sidebar-company-logo" />
        ) : (
          <img src="/k-gestionale.png" alt="K Gestionale" className="h-10 w-10 rounded-md" />
        )}
        <div className="leading-tight min-w-0">
          <div className="font-heading font-extrabold text-[15px] tracking-tight truncate" data-testid="sidebar-company-name">{companyName}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">{simple ? "Gestionale semplice" : "Preventivi"}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.filter((n) => (user?.role === "junior" ? n.to === "/agenda" : n.superOnly ? user?.is_superadmin : !n.adminOnly || user?.role === "admin"))
          .filter((n) => !simple || SIMPLE_PATHS.includes(n.to)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              n.to === "/assistente"
                ? `group flex items-center gap-3 px-3 py-2.5 my-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-500 via-rose-500 to-fuchsia-600 shadow-lg shadow-rose-500/30 transition-[transform,box-shadow] duration-200 hover:scale-[1.02] hover:shadow-rose-500/50 ${isActive ? "ring-2 ring-offset-2 ring-rose-400 ring-offset-card" : ""}`
                : `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className={`w-[18px] h-[18px] ${n.to === "/assistente" ? "group-hover:rotate-12 transition-transform" : ""}`} />
            {simple && n.to === "/impostazioni" ? "Impostazioni" : n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="sm:hidden flex flex-wrap items-center gap-1 px-1 pb-1">
          <LanguageSwitcher />
          {user?.role !== "junior" && (
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setTourOpen(true); }} className="text-muted-foreground gap-1.5" data-testid="mobile-start-tour-button">
              <HelpCircle className="w-[18px] h-[18px]" /> Guida
            </Button>
          )}
          <InstallAppButton variant="ghost" className="h-9 px-3 text-muted-foreground" testid="mobile-install-app-button" />
        </div>
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold text-foreground uppercase">
            {(user?.name || user?.email || "U").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || "Utente"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={doLogout}
            data-testid="logout-button"
            className="text-muted-foreground hover:text-destructive"
          >
            <LogOut className="w-[18px] h-[18px]" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-card border-r border-border animate-in-up">
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </Button>
          {location.pathname !== "/" && user?.role !== "junior" && (
            <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={() => navigate("/")} data-testid="mobile-home-button" aria-label="Home">
              <Home className="w-5 h-5" />
            </Button>
          )}
          <h1 className="font-heading font-bold text-lg sm:text-xl tracking-tight truncate" data-testid="page-title">
            {location.pathname === "/" ? <><span className="lg:hidden">Home</span><span className="hidden lg:inline">Panoramica</span></> : simple && location.pathname === "/impostazioni" ? "Impostazioni" : TITLES[location.pathname] || "Gestionale"}
          </h1>
          <div className="ml-auto flex items-center gap-2">
            {user?.role === "admin" && location.pathname !== "/assistente" && (
              <Button onClick={() => navigate("/assistente")} data-testid="header-agent-button" aria-label="Assistente IA"
                className="h-10 px-3 sm:px-4 rounded-full font-bold text-white border-0 bg-gradient-to-r from-orange-500 via-rose-500 to-fuchsia-600 shadow-md shadow-rose-500/30 hover:shadow-rose-500/50 hover:brightness-110 transition-[filter,box-shadow]">
                <Sparkles className="w-[18px] h-[18px] sm:mr-1.5" /><span className="hidden sm:inline">Assistente</span>
              </Button>
            )}
            <div className="hidden sm:flex items-center gap-2">
              {user?.role !== "junior" && (
                <Button variant="ghost" size="icon" onClick={() => setTourOpen(true)} className="text-muted-foreground" title="Guida" data-testid="start-tour-button">
                  <HelpCircle className="w-[18px] h-[18px]" />
                </Button>
              )}
              <LanguageSwitcher compact />
            </div>
            <NotificationBell />
            <PushToggle />
            <div className="hidden sm:block"><InstallPrompt /></div>
            <span className="hidden sm:inline text-xs text-muted-foreground font-medium px-2.5 py-1 rounded-full bg-secondary" translate="no">
              {LANGS.find((l) => l.code === getLang())?.label} · € EUR
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle-button"
              className="text-muted-foreground"
            >
              {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </Button>
          </div>
        </header>
        <PreviewBanner />
        <TrialBanner company={company} isAdmin={user?.role === "admin"} />
        {offline && (
          <div className="bg-amber-500 text-white text-xs text-center py-1.5 px-4" data-testid="offline-banner">
            Sei offline: stai visualizzando gli ultimi dati salvati sul dispositivo.
          </div>
        )}
        <ServerWakingBanner />
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
        <OnboardingTour open={tourOpen} onClose={closeTour} simple={simple} />
        <footer className="border-t border-border px-4 sm:px-6 py-2 text-sm text-muted-foreground flex flex-wrap items-center justify-center gap-x-2 gap-y-0">
          <span>© {new Date().getFullYear()} K Gestionale · {companyName}</span>
          <Link to="/privacy" className="inline-flex items-center min-h-[44px] px-3 hover:text-foreground hover:underline underline-offset-4" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/termini" className="inline-flex items-center min-h-[44px] px-3 hover:text-foreground hover:underline underline-offset-4" data-testid="footer-terms-link">Termini</Link>
          <Link to="/cookie" className="inline-flex items-center min-h-[44px] px-3 hover:text-foreground hover:underline underline-offset-4" data-testid="footer-cookie-link">Cookie</Link>
        </footer>
      </div>
    </div>
  );
}
